<template>
  <f7-actions
    v-model:opened="opened"
    grid
    class="parts-details-card-quantity-actions"
  >
    <f7-actions-group
      v-if="selectedArticle?.Articulo"
      class="content-modify-quantity"
    >
      <f7-actions-label class="content-modify-quantity__label">
        Ha seleccionado el artículo "<strong>{{ `${selectedArticle.Articulo.Codigo} - ${selectedArticle.Articulo.Nombre}` }}</strong>"
        <br>
        ¿Qué cantidad desea establecer?
      </f7-actions-label>

      <div class="content-modify-quantity__stepper">
        <f7-stepper
          fill
          :manual-input-mode="true"
          :min="10 ** -(selectedArticle.Articulo.PrecisionCantidades)"
          :max="Number.MAX_SAFE_INTEGER"
          :value="initialInputValue"
          :decimal-point="selectedArticle.Articulo.PrecisionCantidades"
          :step="10 ** -(selectedArticle.Articulo.PrecisionCantidades)"
          :format-value="formatInputValue"
          @stepper:change="selectedArticleQuantity = formatInputValue($event)"
        />
      </div>

      <div class="content-modify-quantity__buttons">
        <f7-button
          color="gray"
          @click="opened = false"
        >
          {{ $t('common.cancel') }}
        </f7-button>

        <f7-button
          color="green"
          @click="handleModification"
        >
          {{ $t('common.confirm') }}
        </f7-button>
      </div>
    </f7-actions-group>
  </f7-actions>
</template>

<script setup>
// @ts-check
import { ref, watch } from 'vue';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { callModifyQuantityConsumedArticle } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

/** @import { PropType, Ref } from 'vue' */

/** @import { ParteProduccionRealEntrada } from '@erpagroapp/types' */

const props = defineProps({
  selectedArticle: {
    /** @type {PropType<ParteProduccionRealEntrada>} */
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['success']);

const opened = defineModel('opened', {
  type: Boolean,
  default: false,
});

/** @type {Ref<string>} */
const selectedArticleQuantity = ref('');
/** @type {Ref<number | undefined>} */
const initialInputValue = ref(undefined);

watch(opened, () => {
  initialInputValue.value = opened.value && props.selectedArticle
    ? props.selectedArticle.Cantidad
    : undefined;
});

/**
 * Handles the article quantity edit.
 * @returns {Promise<boolean>} `true` if the operation was successful, `false` otherwise.
 */
async function modifyQuantityConsumedArticle() {
  if (!props.selectedArticle || !selectedArticleQuantity.value) {
    return false;
  }

  let successful = false;

  HiDialog.Preloader(true, 'Modificando la cantidad...');

  try {
    const { data } = await callModifyQuantityConsumedArticle({
      ...props.selectedArticle,
      Cantidad: Number(selectedArticleQuantity.value),
    });

    if (data.Valor === true) {
      successful = true;
      HiNotification.Toast('La cantidad del artículo se ha modificado correctamente');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido modificar la cantidad del artículo');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido modificar la cantidad del artículo');
  } finally {
    HiDialog.Preloader(false);
  }

  return successful;
}

async function handleModification() {
  opened.value = false;

  const successful = await modifyQuantityConsumedArticle();
  if (successful) {
    emit('success');
  }
}

/**
 * Updates the amount input.
 * @param {string} value - The current value.
 * @returns {string} The new value.
 */
function formatInputValue(value) {
  const precision = props.selectedArticle.Articulo?.PrecisionCantidades ?? 0;
  if (value === '') {
    return '';
  }

  const numericValue = Number(value);
  return Number.isInteger(numericValue) ? numericValue.toString() : numericValue.toFixed(precision);
}
</script>

<style scoped lang="scss">
.parts-details-card-quantity-actions {
  .content-modify-quantity {
    &__label {
      display: block;
      text-align: center;
      color: var(--his-oliva);
      text-wrap: balance;
      margin-top: var(--f7-typography-margin);
    }

    &__stepper {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    }

    &__buttons {
      display: flex;
      justify-content: space-between;
      margin: var(--f7-typography-margin);
      width: 100%;
    }
  }
}
</style>
